/**
 * Phase 125 — Celebratory activation overlay.
 *
 * Shown after a successful host registration.  Operators don't see
 * "Created vendor profile" — they see "Your academy is now part of
 * ArenaX Bengaluru."
 *
 * Dismiss via Continue → onContinue callback (parent decides where to
 * route).  CTA copy is intentionally future-tense and city-rooted.
 */
import { CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { ArenaxWordmark } from './ArenaxWordmark';

const COPY = {
  academy: {
    headline: (city) => `Your academy is now part of ArenaX ${city || 'India'}.`,
    sub: 'Students, leagues, and city games will start finding you. Build the rituals.',
    badge: 'ACADEMY ACTIVATED',
  },
  venue: {
    headline: (city) => `Your turf is now discoverable across ${city || 'the city'}.`,
    sub: 'Players nearby can book your slots. Show up in the city pulse tonight.',
    badge: 'VENUE LIVE',
  },
  organizer: {
    headline: () => 'Your league can now shape the city\'s rhythm.',
    sub: 'Recurring tournaments. Defended champions. The rivalries that turn into mythology.',
    badge: 'ORGANIZER ACTIVATED',
  },
};

export default function ArenaxActivationCelebration({ kind, city, onContinue, continueLabel = 'Continue' }) {
  const copy = COPY[kind] || COPY.academy;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-5 arenax-themed animate-in fade-in duration-500"
      style={{
        background: 'var(--ax-bg-gradient)',
        backdropFilter: 'blur(8px)',
      }}
      data-testid={`arenax-activation-celebration-${kind}`}
    >
      <div className="text-center max-w-lg">
        {/* Brand block */}
        <div className="inline-flex items-center justify-center mb-5">
          <ArenaxWordmark size="lg" />
        </div>

        {/* Earned green seal */}
        <div className="relative inline-flex items-center justify-center mb-6">
          <div
            className="h-20 w-20 rounded-full flex items-center justify-center relative"
            style={{
              background: 'radial-gradient(circle, rgba(16,185,129,0.20), rgba(16,185,129,0.05))',
              boxShadow: '0 0 0 1px rgba(16,185,129,0.30), 0 0 36px rgba(16,185,129,0.18)',
            }}
          >
            <CheckCircle2 className="h-9 w-9" style={{ color: '#10b981' }} />
            <Sparkles
              className="h-4 w-4 absolute"
              style={{ top: 6, right: 4, color: '#34d399' }}
            />
          </div>
        </div>

        {/* Status eyebrow */}
        <div
          className="text-[10px] uppercase tracking-[0.4em] mb-3 font-semibold"
          style={{ color: '#047857' }}
          data-testid={`arenax-celebration-eyebrow-${kind}`}
        >
          {copy.badge}
        </div>

        {/* Headline */}
        <h1
          className="text-2xl sm:text-3xl font-light tracking-tight mb-3"
          style={{
            color: 'hsl(var(--ax-text))',
            fontFamily: 'Georgia, "Times New Roman", serif',
          }}
          data-testid={`arenax-celebration-headline-${kind}`}
        >
          {copy.headline(city)}
        </h1>

        {/* Sub */}
        <p
          className="text-sm mb-8 max-w-md mx-auto leading-relaxed"
          style={{ color: 'hsl(var(--ax-text-muted))' }}
        >
          {copy.sub}
        </p>

        {/* Continue CTA */}
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all hover:-translate-y-0.5"
          style={{
            background: 'var(--ax-primary-gradient)',
            color: 'hsl(var(--ax-button-text))',
            boxShadow: 'var(--ax-shadow-glow)',
          }}
          data-testid={`arenax-celebration-continue-${kind}`}
        >
          {continueLabel} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
